import { useMutation, useQueryClient } from "@tanstack/react-query";
import { API_BASE, handleResponse } from "./common";
import type { LogoKind } from "./types";
import { fetchWithInterceptor } from "./client";

// The list cache each logo kind belongs to. A logo change re-stamps the
// entity's `updated` server-side, and `updated` is what avatar URLs cache-bust
// on — so both this list and the shared registry have to be refetched or every
// avatar keeps pointing at the previous image.
const LIST_KEY: Record<LogoKind, string> = {
  source: "sources",
  group: "groups",
  category: "categories",
  "expense-type": "expense-types",
  payee: "payees",
};

/**
 * Where an entity's logo is served, versioned by the entity's `updated` stamp.
 *
 * Logos are cached for a day, so a URL that stays the same keeps showing the
 * previous image after an upload or a reset. Both re-stamp `updated`
 * server-side, so the version changes exactly when the image does and the
 * long max-age is safe. Every logo `<img>` builds its URL here — an unversioned
 * one anywhere is a chip that stays stale after the change.
 */
export const logoUrl = (
  kind: LogoKind,
  entity: { id: string; updated: string },
): string =>
  `${API_BASE}/logo/${kind}/${entity.id}?v=${encodeURIComponent(entity.updated)}`;

export const uploadLogo = async (
  kind: LogoKind,
  id: string,
  file: Blob,
): Promise<void> => {
  const response = await fetchWithInterceptor(
    `${API_BASE}/logo/${kind}/${id}`,
    {
      method: "PUT",
      body: file,
    },
  );
  await handleResponse(response);
};

export const resetLogo = async (kind: LogoKind, id: string): Promise<void> => {
  const response = await fetchWithInterceptor(
    `${API_BASE}/logo/${kind}/${id}`,
    {
      method: "DELETE",
    },
  );
  await handleResponse(response);
};

const useInvalidateAfterLogoChange = () => {
  const queryClient = useQueryClient();
  return (kind: LogoKind) => {
    queryClient.invalidateQueries({ queryKey: [LIST_KEY[kind]] });
    queryClient.invalidateQueries({ queryKey: ["registry"] });
  };
};

export const useUploadLogo = () => {
  const invalidate = useInvalidateAfterLogoChange();
  return useMutation({
    mutationFn: ({
      kind,
      id,
      file,
    }: {
      kind: LogoKind;
      id: string;
      file: Blob;
    }) => uploadLogo(kind, id, file),
    onSuccess: (_data, { kind }) => invalidate(kind),
  });
};

export const useResetLogo = () => {
  const invalidate = useInvalidateAfterLogoChange();
  return useMutation({
    mutationFn: ({ kind, id }: { kind: LogoKind; id: string }) =>
      resetLogo(kind, id),
    onSuccess: (_data, { kind }) => invalidate(kind),
  });
};
